import {
    buildPaymentReturnOnboardingSuccessRoute,
    buildPaymentReturnUrl,
    normalizePaymentReturnPath,
    PAYMENT_RETURN_UPGRADE_PATH,
} from '@/pages/PaymentReturn/paymentReturnFlow';
import { writePaymentReturnWorkspaceId } from '@/utils/paymentReturnWorkspace';

export const isStripeHostedInvoiceUrl = (paymentUrl?: string | null): boolean => {
    if (!paymentUrl) {
        return false;
    }

    try {
        const parsedUrl = new URL(paymentUrl);
        return parsedUrl.hostname === 'invoice.stripe.com';
    } catch {
        return false;
    }
};

interface RuntimeAccessOptions {
    disableCloudRuntime?: boolean;
}

interface BuildCheckoutReturnUrlsParams extends RuntimeAccessOptions {
    currentUrl: string;
    mode: 'onboarding' | 'upgrade';
    planId?: string | null;
    planCode: string;
    planName: string;
    billingCycle?: string | null;
    workspaceId?: string;
    onboardingTrialSetup?: boolean;
}

interface BuildOnboardingAdminSuccessRouteParams extends RuntimeAccessOptions {
    workspaceId: string;
    planCode?: string | null;
    planName?: string | null;
    billingCycle?: string | null;
    onboardingTrialSetup?: boolean;
}

export const buildOnboardingAdminSuccessRoute = ({
    workspaceId,
    planCode,
    planName,
    billingCycle,
    onboardingTrialSetup,
    disableCloudRuntime,
}: BuildOnboardingAdminSuccessRouteParams): string => {
    writePaymentReturnWorkspaceId(workspaceId);
    return buildPaymentReturnOnboardingSuccessRoute({
        workspaceId,
        planCode,
        planName,
        billingCycle,
        onboardingTrialSetup,
        disableCloudRuntime,
    });
};

export const buildCheckoutReturnUrls = ({
    currentUrl,
    mode,
    planId,
    planCode,
    planName,
    billingCycle,
    workspaceId,
    onboardingTrialSetup,
}: BuildCheckoutReturnUrlsParams): { successUrl: string; cancelUrl: string } => {
    const parsedCurrentUrl = new URL(currentUrl);
    const resolvedWorkspaceId = workspaceId || parsedCurrentUrl.searchParams.get('workspace_id') || undefined;
    const returnPath = mode === 'upgrade'
        ? PAYMENT_RETURN_UPGRADE_PATH
        : normalizePaymentReturnPath(parsedCurrentUrl.pathname);

    if (resolvedWorkspaceId) {
        writePaymentReturnWorkspaceId(resolvedWorkspaceId);
    }

    return {
        successUrl: buildPaymentReturnUrl({
            origin: parsedCurrentUrl.origin,
            kind: 'subscription',
            result: 'success',
            mode,
            returnPath,
            workspaceId: resolvedWorkspaceId,
            planId,
            planCode,
            planName,
            billingCycle,
            includeCheckoutSessionId: true,
            onboardingTrialSetup,
        }),
        cancelUrl: buildPaymentReturnUrl({
            origin: parsedCurrentUrl.origin,
            kind: 'subscription',
            result: 'cancel',
            mode,
            returnPath,
            workspaceId: resolvedWorkspaceId,
            planId,
            planCode,
            planName,
            billingCycle,
            onboardingTrialSetup,
        }),
    };
};
